// src/components/uniform-gallery/uniform-gallery.ts

import type { IUniformGallery } from './uniform-gallery.types'
import type {
  ResponsiveImage,
  GalleryImageData,
  IImageViewer
} from '../gallery/gallery.types'
import { PictureElementFactory } from '../../utils/picture-element-factory'
import type { IGalleryDataService } from '../../services/gallery-data.types'
import { StaticManifestGalleryDataService } from '../../services/static-manifest.gallery-data.service'
import { ImageErrorHandler } from '../../utils/image-error-handler'
import './uniform-gallery.css'
import { computeRows } from './uniform-gallery.layout'

export class UniformGallery extends HTMLElement implements IUniformGallery {
  private static readonly REVEAL_MARGIN = '50px'
  private static readonly HIGH_PRIORITY_IMAGE_COUNT = 6
  private static readonly LAYOUT_GAP = 10
  private static readonly MOBILE_BREAKPOINT = 576
  private static layoutRafPending = false

  private images: ResponsiveImage[] = []
  private cachedImageData: GalleryImageData[] | null = null
  private isInitialized = false
  private viewer: IImageViewer | null = null

  private _dataService: IGalleryDataService | null = null
  private errorHandler = new ImageErrorHandler()

  set dataService(service: IGalleryDataService) {
    this._dataService = service
  }

  // Shared static observers
  private static sharedRevealObserver?: IntersectionObserver
  private static sharedResizeObserver?: ResizeObserver
  private static observedGalleries = new Set<UniformGallery>()

  connectedCallback() {
    // setTimeout(0) fires after deferred module scripts — gives main.ts a chance to
    // inject a service before the fallback fires; also handles pre-connection injection
    setTimeout(() => {
      if (!this.isInitialized) {
        if (!this._dataService) {
          const url = this.getAttribute('data-manifest-url') || '/gallery-data.json'
          this._dataService = new StaticManifestGalleryDataService(url)
        }
        this.isInitialized = true
        this.runInitialize()
      }
    }, 0)
  }

  disconnectedCallback() {
    this.cleanup()
  }

  private async runInitialize(): Promise<void> {
    this.className = 'uniform-gallery'
    this.setupRevealObserver()
    await this.loadImages()
    if (!this.images.length) {
      this.dispatchEvent(new CustomEvent('uniform-gallery:error', {
        detail: { message: 'Failed to load gallery' }
      }))
      return
    }
    this.renderImages()
    this.setupViewerIntegration()
    this.dispatchEvent(new CustomEvent('uniform-gallery:initialized'))
  }

  private async loadImages(): Promise<void> {
    if (!this._dataService) {
      this.showError('Failed to load gallery')
      return
    }
    try {
      this.images = await this._dataService.getImages()
    } catch {
      this.showError('Failed to load gallery')
    }
  }

  private renderImages(): void {
    this.innerHTML = ''

    this.images.forEach((image, index) => {
      const item = this.createItem(image, index)
      this.appendChild(item)
    })

    const spacer = document.createElement('div')
    spacer.className = 'gallery-spacer'
    this.appendChild(spacer)

    this.applyLayout(this.clientWidth)
  }

  private getLayoutParams(containerWidth: number): { targetHeight: number; minRowRatio: number } {
    if (containerWidth >= 1200) return { targetHeight: 420, minRowRatio: 0.65 }
    if (containerWidth >= 900) return { targetHeight: 360, minRowRatio: 0.65 }
    return { targetHeight: 300, minRowRatio: 0.65 }
  }

  private applyLayout(containerWidth: number): void {
    const items = Array.from(this.querySelectorAll<HTMLElement>('.gallery-item'))
    if (!items.length) return

    if (containerWidth <= UniformGallery.MOBILE_BREAKPOINT) {
      items.forEach(item => {
        item.style.removeProperty('width')
        item.style.removeProperty('height')
        item.style.removeProperty('flex-basis')
        item.style.removeProperty('flex-grow')
      })
      return
    }

    const { targetHeight, minRowRatio } = this.getLayoutParams(containerWidth)
    const rows = computeRows(
      this.images.map(img => img.aspectRatio),
      containerWidth,
      targetHeight,
      UniformGallery.LAYOUT_GAP,
      minRowRatio
    )

    let itemIndex = 0
    for (const row of rows) {
      for (const layoutItem of row.items) {
        const el = items[itemIndex++]
        if (!el) continue
        el.style.width = `${layoutItem.width}px`
        el.style.height = `${row.height}px`
        el.style.flexBasis = `${layoutItem.width}px`
        el.style.flexGrow = '0'
      }
    }
  }

  private createItem(image: ResponsiveImage, index: number): HTMLElement {
    const item = document.createElement('div')
    item.className = 'gallery-item'
    item.setAttribute('tabindex', '0')
    item.setAttribute('role', 'button')
    item.setAttribute('aria-label', `View ${image.alt}`)
    item.style.setProperty('--ar', image.aspectRatio.toString())

    const wrapper = document.createElement('div')
    item.appendChild(wrapper)

    // LQIP placeholder
    this.addLQIPPlaceholder(image, wrapper)

    const picture = PictureElementFactory.create(image, 'gallery', {
      originalIndex: index,
      highPriority: index < UniformGallery.HIGH_PRIORITY_IMAGE_COUNT
    })

    const img = picture.querySelector('img')
    if (img) {
      img.onload = () => {
        const reveal = () => {
          const placeholder = wrapper.querySelector('[data-placeholder]')
          if (placeholder) {
            (placeholder as HTMLElement).style.opacity = '0'
            setTimeout(() => placeholder.remove(), 300)
          }
          wrapper.classList.add('loaded')
          this.dispatchEvent(new CustomEvent('uniform-gallery:image-loaded', {
            detail: { image }
          }))
        }
        // Cached images fire onload synchronously before the item is in the DOM,
        // so wrapper has no painted initial state and the CSS transition never plays.
        // setTimeout(0) waits past DOM insertion; rAF ensures one paint cycle fires
        // with opacity:0 / scale(0.92) before the class change triggers the transition.
        if (!wrapper.isConnected) {
          setTimeout(() => requestAnimationFrame(reveal), 0)
        } else {
          requestAnimationFrame(reveal)
        }
      }

      img.onerror = async () => {
        await this.errorHandler.handleImageError(image, img)
      }
    }

    wrapper.appendChild(picture)

    // Above-fold images reveal immediately; others use observer
    if (index < UniformGallery.HIGH_PRIORITY_IMAGE_COUNT) {
      item.classList.add('visible')
    } else {
      UniformGallery.sharedRevealObserver?.observe(item)
    }

    // Keyboard support (Enter/Space opens viewer)
    item.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        item.click()
      }
    })

    return item
  }

  // ── Public API ────────────────────────────────────────

  getImages(): GalleryImageData[] {
    if (this.cachedImageData) return this.cachedImageData

    this.cachedImageData = this.images.map((image, index) => {
      const jpegSource = image.sources.find(s => s.format === 'jpeg')
      const webpSource = image.sources.find(s => s.format === 'webp')
      const jpegSrcset = jpegSource?.sizes.map(s => `${s.url} ${s.width}w`).join(', ') || ''
      const webpSrcset = webpSource?.sizes.map(s => `${s.url} ${s.width}w`).join(', ') || ''

      return {
        id: image.id,
        src: jpegSource?.sizes[0]?.url || '',
        srcset: jpegSrcset,
        webpSrcset,
        alt: image.alt,
        width: image.metadata.originalWidth,
        height: image.metadata.originalHeight,
        index
      }
    })

    return this.cachedImageData
  }

  // ── Observers ─────────────────────────────────────────

  private setupRevealObserver(): void {
    if (!UniformGallery.sharedRevealObserver) {
      UniformGallery.sharedRevealObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return
          entry.target.classList.add('visible')
          UniformGallery.sharedRevealObserver?.unobserve(entry.target)
        })
      }, { rootMargin: UniformGallery.REVEAL_MARGIN })
    }
  }

  // ── Viewer Integration ────────────────────────────────

  private setupViewerIntegration(): void {
    this.setupViewer()
    this.setupClickHandler()
    this.setupResizeObserver()
  }

  private isMobile(): boolean {
    return window.matchMedia('(max-width: 36rem)').matches
  }

  private setupViewer(): void {
    this.viewer = document.querySelector('image-viewer') as IImageViewer | null
    if (this.viewer) {
      this.viewer.setImages(this.getImages())
      this.viewer.setEnabled(!this.isMobile())
    }
  }

  private setupClickHandler(): void {
    this.addEventListener('click', (e) => {
      const item = (e.target as HTMLElement).closest('.gallery-item') as HTMLElement
      if (!item) return
      const img = item.querySelector('img')
      if (!img) return
      const images = this.getImages()
      const index = images.findIndex(d => d.id === img.dataset.id)
      if (index < 0) return
      this.dispatchEvent(new CustomEvent('uniform-gallery:image-click', {
        detail: { index, images },
        bubbles: true
      }))
      if (this.viewer?.getState().enabled) {
        this.viewer.setImages(images)
        this.viewer.open(index)
      }
    })
  }

  private setupResizeObserver(): void {
    if (!UniformGallery.sharedResizeObserver) {
      UniformGallery.sharedResizeObserver = new ResizeObserver((entries) => {
        if (!UniformGallery.layoutRafPending) {
          UniformGallery.layoutRafPending = true
          requestAnimationFrame(() => {
            UniformGallery.layoutRafPending = false
            entries.forEach(entry => {
              const gallery = entry.target as UniformGallery
              gallery.applyLayout(entry.contentRect.width)
              if (gallery.viewer) gallery.viewer.setEnabled(!gallery.isMobile())
            })
          })
        }
      })
    }
    UniformGallery.observedGalleries.add(this)
    UniformGallery.sharedResizeObserver.observe(this)
  }

  // ── Error ─────────────────────────────────────────────

  private showError(message: string): void {
    const container = document.createElement('div')
    container.className = 'uniform-gallery-error'
    container.textContent = message
    this.innerHTML = ''
    this.appendChild(container)
  }

  // ── LQIP ─────────────────────────────────────────────

  private addLQIPPlaceholder(image: ResponsiveImage, wrapper: HTMLElement): void {
    if (!image.lqip || typeof image.lqip !== 'object') return
    const placeholder = document.createElement('div')
    placeholder.setAttribute('data-placeholder', 'true')
    placeholder.setAttribute('data-lqip-type', image.lqip.type)
    if (image.lqip.type === 'solid-color') {
      if (image.lqip.data.startsWith('data:image/svg+xml')) {
        placeholder.style.backgroundImage = `url("${image.lqip.data}")`
      } else {
        placeholder.style.backgroundColor = image.lqip.dominantColor || '#f0f0f0'
      }
    } else if (image.lqip.type === 'blur') {
      placeholder.style.backgroundImage = `url("${image.lqip.data}")`
    }
    wrapper.appendChild(placeholder)
  }

  // ── Cleanup ───────────────────────────────────────────

  private cleanup(): void {
    UniformGallery.observedGalleries.delete(this)
    UniformGallery.sharedResizeObserver?.unobserve(this)
    if (UniformGallery.observedGalleries.size === 0) {
      UniformGallery.sharedRevealObserver?.disconnect()
      delete UniformGallery.sharedRevealObserver
      UniformGallery.sharedResizeObserver?.disconnect()
      delete UniformGallery.sharedResizeObserver
      UniformGallery.layoutRafPending = false
    }
    this.isInitialized = false
    this.cachedImageData = null
  }
}

if (!customElements.get('uniform-gallery')) {
  customElements.define('uniform-gallery', UniformGallery)
}
